import React, {
  FC, useState, useRef,
} from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { Styling } from '../../../../../constants';
import { Colors } from '../../../../../constants/colors';
import {
  enableClipperKeyboardControls,
  disableClipperKeyboardControls,
} from '../../../state/videoPlayer';
import { IAppState } from '../../../../../state/reducers';

interface IOwnProps {
  name: string;
  onChange: (value: string) => void;
  isDisabled: boolean;
}

interface IStateProps { }

interface IDispatchProps {
  disableClipperKeyboardControls: () => void;
  enableClipperKeyboardControls: () => void;
}

interface IProps extends IOwnProps, IStateProps, IDispatchProps { }

interface INameInputProps {
  disabled?: boolean;
}

const NameInput = styled.input<INameInputProps>`
  background: none;
  border: none;
  border-radius: ${Styling.DEFAULT_BORDER_RADIUS}px;
  font-size: 14px;
  font-weight: bold;
  padding: 0;

  &:hover, &:focus {
    background: ${(props) => (!props.disabled && Colors.BLACK_10)};
  }
`;

const NameEditor: FC<IProps> = (props: IProps) => {
  const [editingName, setEditingName] = useState<string | null>(props.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    isDisabled,
  } = props;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingName(e.target.value);
  };

  const handleMouseDownOutside = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target !== inputRef.current) {
      // We manually blur the input here because react-beautiful-dnd prevents some default behavior of events which
      // in the end prevents the input blur when a draggable element containing the input is clicked.
      // A related issue is described here - https://github.com/atlassian/react-beautiful-dnd/issues/1645.
      inputRef.current.blur();
      const {
        enableClipperKeyboardControls,
      } = props;
      enableClipperKeyboardControls();
      document.removeEventListener('mousedown', handleMouseDownOutside as unknown as EventListener);
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Set focus in state
    const {
      disableClipperKeyboardControls,
    } = props;
    disableClipperKeyboardControls();
    e.target.select();
    document.addEventListener('mousedown', handleMouseDownOutside as unknown as EventListener);
  };

  const handleBlur = () => {
    const {
      onChange,
      name,
    } = props;
    const { value } = inputRef.current;

    if (value.trim() !== '') onChange(value);
    else setEditingName(name);
  };

  return (
    <NameInput
      ref={inputRef}
      value={editingName}
      onChange={handleNameChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={isDisabled}
    />
  );
};

const mapStateToProps = (state: IAppState): IStateProps => ({});

const mapDispatchToProps: IDispatchProps = {
  disableClipperKeyboardControls,
  enableClipperKeyboardControls,
};

export default connect(mapStateToProps, mapDispatchToProps)(NameEditor);
